import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Select, MenuItem, FormControl, InputLabel, Pagination, Stack, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { apiService } from '../../services/apiService';

const STATUS_OPTS = ['new','in_progress','closed'];

const SupportMessagesAdmin: React.FC = () => {
  const { keycloak, initialized } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdministrator = useMemo(() => {
    try {
      const token: any = keycloak?.tokenParsed || {};
      const groups: string[] = (token?.groups || []) as string[];
      const ADMIN_NAME = (process.env.REACT_APP_ADMIN_GROUP_NAME || 'administrator');
      return Array.isArray(groups) && groups.some(g => String(g).replace(/^\//, '') === ADMIN_NAME);
    } catch {
      return false;
    }
  }, [keycloak]);

  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));

  const load = async () => {
    setLoading(true); setError(null);
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      const resp = await apiService.listSupportMessages({ status: statusFilter || undefined, limit, offset });
      setItems(resp.data?.items || []);
      setTotal(resp.data?.total || 0);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load support messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdministrator) load(); }, [isAdministrator, limit, offset, statusFilter]);

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      await apiService.updateSupportMessage(id, { status: newStatus });
      // Optimistically update UI
      setItems(prev => prev.map(it => (it.id === id ? { ...it, status: newStatus } : it)));
    } catch (e) {
      // ignore; will pick up on next load
    }
  };

  if (!initialized) return <LoadingSpinner />;
  if (!isAdministrator) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Administrator Access Required</Typography>
        <Typography variant="body1">You must be an administrator to view support messages.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>User Support Messages</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select labelId="status-filter-label" label="Status" value={statusFilter} onChange={(e) => { setOffset(0); setStatusFilter(e.target.value); }}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">Total: {total}</Typography>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>{it.id}</TableCell>
                  <TableCell>{it.created_at ? new Date(it.created_at).toLocaleString() : ''}</TableCell>
                  <TableCell>{it.name || '-'}</TableCell>
                  <TableCell>{it.email || '-'}</TableCell>
                  <TableCell>{it.subject || '-'}</TableCell>
                  <TableCell>{String(it.message || '').slice(0, 140)}</TableCell>
                  <TableCell>
                    <Select size="small" value={it.status || 'new'} onChange={(e) => handleStatusChange(it.id, e.target.value)}>
                      {STATUS_OPTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ p: 1 }}>
            <Pagination count={pageCount} page={page} onChange={(_, p) => setOffset((p - 1) * limit)} />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default SupportMessagesAdmin;
